import React, { useState, useRef, useCallback } from 'react';
import Axios from 'axios';
import styles from './AddTeamModal.module.css';
import { Add, Close, Refresh, Visibility, VisibilityOff } from '@mui/icons-material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { API_URL } from '../../utils/auth';
import Auth from '../../utils/auth';
import { useEffect } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import { FormControlLabel } from '@mui/material';
import { TextField } from '@mui/material';
import { toast } from 'react-toastify';
import Cropper from 'react-easy-crop';
import { generatePassword } from '../../utils/passwordUtils';
import { getCroppedImg } from '../../utils/imageUtils';

const AddTeamModal = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  passwordType,
  setPasswordType,
  manualPassword,
  setManualPassword,
  generatedPassword,
  setGeneratedPassword
}) => { 

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState ('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(true); 
  
  // Image cropping states
  const [profileImage, setProfileImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);
  
  const handleSubmit =  async (e) => {

    e.preventDefault();

    const workspaceId = Auth.getWorkspaceId();
    const userId = Auth.getUser()?.id;

    const body = {
      email,
      password: passwordType === 'auto' ? generatedPassword : manualPassword,
      firstName, 
      lastName,
      phoneNumber: phone,
      workspaceId,
      userId,
      admin: isAdmin, // Use the state value
      mustChangePassword
    }; 

    // Add image data if profile image is selected
    if (profileImage && profileImage !== originalImage) {
      const base64Data = profileImage.split(',')[1];
      const fileName = `profile_${Date.now()}.jpg`;
      body.fileBuffer = base64Data;
      body.fileName = fileName;
      body.mimeType = 'image/jpeg';
    }

    try {
      const { data } = await Axios.post(
        `${API_URL}/user?workspaceId=${workspaceId}&userId=${userId}`,
        body
      );
      if (!data.error) {
        onSubmit({ email: email, password: passwordType === 'auto' ? generatedPassword : manualPassword }); 
        onClose();
      } else {
        toast.error(data.error || 'Error creating user');
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || 'Error creating user';
      toast.error(msg);
    }

  };

  const generateAndSetPassword = () => {
    const password = generatePassword(12);
    setGeneratedPassword(password);
  };

  // State to manage password visibility
  const [showPassword, setShowPassword] = useState(false);

  // Function to toggle password visibility
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handlePasswordTypeChange = (e) => {
    setPasswordType(e.target.value);
    if (e.target.value === 'auto') {
      generateAndSetPassword();
    }
  };

  const handleGeneratePassword = () => {
    generateAndSetPassword();
  };

  const handleChange = (e) => {
    setManualPassword(e.target.value);
  };

  // Image handling functions
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) {
      toast.warn('File size exceeds 2.5 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setOriginalImage(ev.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropAndUpload = async () => {
    if (!originalImage || !croppedAreaPixels) return;
    try {
      const croppedBase64 = await getCroppedImg(originalImage, croppedAreaPixels, zoom);
      setProfileImage(croppedBase64);
      setShowCropper(false);
      toast.success('Image cropped successfully!');
      fileInputRef.current.value = null;
    } catch (error) {
      console.error('Error cropping image', error);
      toast.error('Error cropping image');
      fileInputRef.current.value = null;
    }
  };

  const handleUploadOriginal = async () => {
    if (!originalImage) return;
    setProfileImage(originalImage);
    setShowCropper(false);
    toast.success('Image uploaded successfully!');
    fileInputRef.current.value = null;
  };

  const handleCameraClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setIsAdmin(false);
      setMustChangePassword(true);
      setPasswordType('');
      setManualPassword('');
      setGeneratedPassword('');
      setShowPassword(false);
      setProfileImage(null);
      setOriginalImage(null);
      setShowCropper(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <div className={styles.modalOverlay} >
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Add New Team Member</h2>
              <button className={styles.closeButton} onClick={onClose}>
                <Close fontSize="small" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.formContainer}>
              <div className={styles.profileSection}>
                <div className={styles.profileImageContainer}>
                  <div className={styles.profileImage}>
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                      />
                    ) : (
                      <div className={styles.defaultAvatar}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="7" r="5" />
                          <path d="M20 21v-2a7 7 0 0 0-14 0v2" />
                        </svg>
                      </div>
                    )}
                    <div className={styles.cameraIcon} onClick={handleCameraClick} title="Change profile image">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleImageChange}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.profileInfo}>
                  <h3>Profile Image</h3>
                  <p>The proposed size is 512×512 px no bigger than 2.5 MB</p>
                </div>
              </div>

              <div className={styles.formFields}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="firstName">First Name <span className={styles.required}>*</span></label>
                    <input
                      type="text"
                      id="firstName"
                      placeholder="First Name"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="lastName">Last Name <span className={styles.required}>*</span></label>
                    <input 
                      type="text" 
                      id="lastName" 
                      placeholder="Last Name" 
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      required 
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="email">Email <span className={styles.required}>*</span></label>
                    <input
                      type="email"
                      id="email"
                      placeholder="Email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      placeholder="Phone"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                    />
                  </div>
                </div>

                {/* Password Section */}
                <div className={styles.passwordSection}>
                  <div className={styles.passwordOptions}>
                    <div className={styles.passwordOption}>
                      <label style={{ fontWeight: 500, marginRight: 12 }}>Password Type:</label>
                      <FormControlLabel
                        control={<input type="radio" name="passwordType" value="auto" checked={passwordType === 'auto'} onChange={handlePasswordTypeChange} />}
                        label="Auto-generate"
                      />
                      <FormControlLabel
                        control={<input type="radio" name="passwordType" value="manual" checked={passwordType === 'manual'} onChange={handlePasswordTypeChange} />}
                        label="Manual"
                      />
                    </div>
                  </div>
                  {passwordType === 'auto' && (
                    <div className={styles.passwordDisplay}>
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Password"
                        value={generatedPassword}
                        type={showPassword ? 'text' : 'password'}
                        style={{ flex: 2 }}
                        InputLabelProps={{ shrink: true, style: { top: -10, fontSize: '16px', fontWeight: 600 } }}
                        InputProps={{
                          style: {
                            border: '1.5px solid #572af1',
                            borderRadius: '2px',
                            background: '#fff',
                            padding: '6px 10px',
                            fontSize: '15px',
                          },
                          endAdornment: (
                            <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          )
                        }}
                        margin="normal"
                        disabled
                      />
                      <IconButton
                        className={styles.regenerateBtn}
                        onClick={handleGeneratePassword}
                        size="small"
                        title="Regenerate password"
                        sx={{
                          border: '1px solid #572af1',
                          color: '#572af1',
                          background: '#fff',
                          borderRadius: '6px',
                          padding: '6px',
                          transition: 'background 0.2s, color 0.2s',
                          '&:hover': {
                            background: '#f3f0ff',
                            color: '#291b3b',
                          }
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </div>
                  )}
                  {passwordType === 'manual' && (
                    <div className={styles.passwordDisplay}>
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Password"
                        name="password"
                        value={manualPassword}
                        onChange={handleChange}
                        type={showPassword ? 'text' : 'password'}
                        style={{ flex: 2 }}
                        InputLabelProps={{ shrink: true, style: { top: -10, fontSize: '16px', fontWeight: 600 } }}
                        InputProps={{
                          style: {
                            border: '1.5px solid #572af1',
                            borderRadius: '2px',
                            background: '#fff',
                            padding: '6px 10px',
                            fontSize: '15px',
                          },
                          endAdornment: (
                            <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          )
                        }}
                        margin="normal"
                        required
                      />
                    </div>
                  )}
                </div>

                <div className={styles.accessSettings}>
                  <div className={styles.roleOptions}>
                    <div className={styles.roleOption}>
                      <div className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          id="adminRole"
                          checked={isAdmin}
                          onChange={e => setIsAdmin(e.target.checked)}
                        />
                        <label htmlFor="adminRole"></label>
                      </div>
                      <div className={styles.roleInfo}>
                        <h4 className={styles.adminRole}>Admin</h4>
                      </div>
                    </div>

                    <div className={styles.roleOption}>
                      <div className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          id="changePasswordRole"
                          checked={mustChangePassword}
                          onChange={e => setMustChangePassword(e.target.checked)}
                        />
                        <label htmlFor="changePasswordRole"></label>
                      </div>
                      <div className={styles.roleInfo}>
                        <h4 className={styles.changePasswordRole}>The user must change password</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.formActions}>
                <button type="button" className={styles.cancelButton} onClick={onClose}>
                  Cancel
                </button> 
                <button type="submit" className={styles.saveButton}>
                  <Add fontSize="small" /> Add Team Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cropper Modal */}
      {showCropper && (
        <div className={styles.cropperModal}>
          <div className={styles.cropperContent}>
            <div className={styles.cropperAreaWrapper}>
              <Cropper
                image={originalImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="round"
                showGrid={false}
                objectFit="horizontal-cover"
              />
            </div>
            <div className={styles.cropperZoomControl}>
              <label htmlFor="zoomRange">Zoom</label>
              <input
                id="zoomRange"
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={e => setZoom(Number(e.target.value))}
              />
            </div>
            <div className={styles.cropperActions}>
              <button className={styles.btn} onClick={handleCropAndUpload}>Crop and upload</button>
              <button className={styles.btn} onClick={handleUploadOriginal}>Upload without cropping</button>
              <button className={styles.btn} onClick={() => {setShowCropper(false); fileInputRef.current.value = null;}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddTeamModal; 